import {useEffect, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'

import Style from './style'

import useGetExamFilterList from 'entity/exam/useGetExamFilterList'
import useGetFranchiseManagerTicketList from 'entity/franchise/useGetFranchiseManagerTicketList'
import useGetEventIdxList from 'entity/store/useGetEventIdxList'
import useGetEventNameList from 'entity/store/useGetEventNameList'
import useGetSeedTicketNameList from 'entity/store/useGetSeedTicketNameList'
import useGetSeriesNameList from 'entity/store/useGetSeriesNameList'
import useGetStoreTicketTypes from 'entity/store/useGetStoreTicketTypes'
import useGetTicketTypeIdxList from 'entity/store/useGetTicketTypeIdxList'
import {convertClassificationAsName} from 'shared/constant/lookup/ticketType'
import useSearchLocal from 'shared/model/useSearchLocal'
import DefaultButton from 'shared/ui/DefaultButton'
import InfiniteSelect from 'shared/ui/InfiniteSelect'
import useGetLevelData from './hooks/useGetLevelData'

const Search = ({
	placeholder,
	storeNameFilter = false,
	classificationFilter = false,
	qualificationFilter = false,
	statusFilter = false,
	voucherTypeFilter = false,
	eventIdxFilter = false,
	isUsedFilter = false,
	ticketTypeIdxFilter = false,
	seedTicketNameFilter = false,
	seriesNameFilter = false,
	eventNameFilter = false,
	prefix = '',
	searchGroup = 'default',
	hideSearchInput = false,
	searchInputFullWidth = false,
}) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const searchRef = useRef()
	const statusRef = useRef()
	const storeNameRef = useRef()
	const classificationRef = useRef()
	const franchiseRef = useRef()
	const qualificationRef = useRef()
	const voucherTypeRef = useRef()
	const eventIdxRef = useRef()
	const isUsedRef = useRef()
	const ticketTypeIdxRef = useRef()
	const seedTicketNameRef = useRef()
	const seriesNameRef = useRef()
	const eventNameRef = useRef()

	const [isInitialized, setIsInitialized] = useState(false)

	const [storeNameList] = useGetFranchiseManagerTicketList()
	const [ticketTypesList] = useGetStoreTicketTypes()
	const [eventIdxList] = useGetEventIdxList(eventIdxFilter)
	const [ticketTypeIdxList] = useGetTicketTypeIdxList(ticketTypeIdxFilter)
	const [seedTicketNameList] = useGetSeedTicketNameList(seedTicketNameFilter)
	const [seriesNameList] = useGetSeriesNameList(seriesNameFilter)
	const [eventNameList] = useGetEventNameList(eventNameFilter)
	const [examList, examLoading, examLoadMore, examHasMore] =
		useGetExamFilterList(qualificationFilter)

	const hasFiltersLocal = searchGroup && searchGroup !== 'default'
	const [localSearchData, saveToLocal] = useSearchLocal(searchGroup)
	const getParamName = (paramName) =>
		prefix ? `${prefix}_${paramName}` : paramName

	const getFranchiseValue = () => {
		if (hasFiltersLocal && localSearchData?.franchise) {
			return localSearchData.franchise
		}
		return searchParams.get(getParamName('franchise')) || 'all'
	}

	const currentFranchise = getFranchiseValue()

	const {levelData} = useGetLevelData(
		examList,
		currentFranchise
	)

	const hasFilters =
		storeNameFilter ||
		classificationFilter ||
		qualificationFilter ||
		statusFilter ||
		voucherTypeFilter ||
		eventIdxFilter ||
		isUsedFilter ||
		ticketTypeIdxFilter ||
		seedTicketNameFilter ||
		seriesNameFilter ||
		eventNameFilter

	useEffect(() => {
		if (
			hasFiltersLocal &&
			localSearchData &&
			Object.keys(localSearchData).length > 0
		) {
			const timer = setTimeout(() => {
				if (searchRef.current && localSearchData.search) {
					searchRef.current.value = localSearchData.search
				}

				if (
					qualificationFilter &&
					qualificationRef.current &&
					localSearchData.qualification !== 'all'
				) {
					qualificationRef.current.value = localSearchData.qualification
				}

				if (
					qualificationFilter &&
					franchiseRef.current &&
					localSearchData.franchise !== 'all'
				) {
					franchiseRef.current.value = localSearchData.franchise
				}

				if (
					statusFilter &&
					statusRef.current &&
					localSearchData.status !== 'all'
				) {
					statusRef.current.value = localSearchData.status
				}

				// if (voucherTypeFilter && voucherTypeRef.current && localSearchData.voucherType !== "all") {
				//     voucherTypeRef.current.value = localSearchData.voucherType
				// }

				if (!isInitialized) {
					searchEvent()
					setIsInitialized(true)
				}
			}, 10)

			return () => clearTimeout(timer)
		}
	}, [hasFiltersLocal, localSearchData, isInitialized])

	const searchEvent = () => {
		const newParams = new URLSearchParams(searchParams)

		newParams.set(getParamName('page'), '1')

		const searchValue = searchRef.current?.value.trim() || ''
		const storeNameValue = storeNameRef.current?.value || 'all'
		const classificationValue = classificationRef.current?.value || 'all'
		const qualificationValue = qualificationRef.current?.value || 'all'
		const franchiseValue = franchiseRef.current?.value || 'all'
		const statusValue = statusRef.current?.value || 'all'
		const voucherTypeValue = voucherTypeRef.current?.value || 'all'
		const eventIdxValue = eventIdxRef.current?.value || 'all'
		const isUsedValue = isUsedRef.current?.value || 'all'
		const ticketTypeIdxValue = ticketTypeIdxRef.current?.value || 'all'
		const seedTicketNameValue = seedTicketNameRef.current?.value || 'all'
		const seriesNameValue = seriesNameRef.current?.value || 'all'
		const eventNameValue = eventNameRef.current?.value || 'all'

		if (hasFiltersLocal) {
			saveToLocal('search', searchValue)
			if (qualificationFilter) saveToLocal('qualification', qualificationValue)
			if (qualificationFilter) saveToLocal('franchise', franchiseValue)
			if (statusFilter) saveToLocal('status', statusValue)
			// if (voucherTypeFilter) saveToLocal("voucherType", voucherTypeValue)
		}

		if (searchValue) {
			newParams.set(getParamName('search'), searchValue)
		} else {
			newParams.delete(getParamName('search'))
		}

		if (storeNameFilter && storeNameValue !== 'all') {
			newParams.set(getParamName('storeName'), storeNameValue)
		} else {
			newParams.delete(getParamName('storeName'))
		}

		if (classificationFilter && classificationValue !== 'all') {
			newParams.set(getParamName('classification'), classificationValue)
		} else {
			newParams.delete(getParamName('classification'))
		}

		if (qualificationFilter && qualificationValue !== 'all') {
			newParams.set(getParamName('qualification'), qualificationValue)
		} else {
			newParams.delete(getParamName('qualification'))
		}

		if (qualificationFilter && franchiseValue !== 'all') {
			newParams.set(getParamName('franchise'), franchiseValue)
		} else {
			newParams.delete(getParamName('franchise'))
		}

		if (statusFilter && statusValue !== 'all') {
			newParams.set(getParamName('status'), statusValue)
		} else {
			newParams.delete(getParamName('status'))
		}

		if (voucherTypeFilter && voucherTypeValue !== 'all') {
			newParams.set(getParamName('voucherType'), voucherTypeValue)
		} else {
			newParams.delete(getParamName('voucherType'))
		}

		if (eventIdxFilter && eventIdxValue !== 'all') {
			newParams.set(getParamName('eventIdx'), eventIdxValue)
		} else {
			newParams.delete(getParamName('eventIdx'))
		}

		if (isUsedFilter && isUsedValue !== 'all') {
			newParams.set(getParamName('isUsed'), isUsedValue)
		} else {
			newParams.delete(getParamName('isUsed'))
		}

		if (ticketTypeIdxFilter && ticketTypeIdxValue !== 'all') {
			newParams.set(getParamName('ticketTypeIdx'), ticketTypeIdxValue)
		} else {
			newParams.delete(getParamName('ticketTypeIdx'))
		}

		if (seedTicketNameFilter && seedTicketNameValue !== 'all') {
			newParams.set(getParamName('seedTicketName'), seedTicketNameValue)
		} else {
			newParams.delete(getParamName('seedTicketName'))
		}

		if (seriesNameFilter && seriesNameValue !== 'all') {
			newParams.set(getParamName('seriesName'), seriesNameValue)
		} else {
			newParams.delete(getParamName('seriesName'))
		}

		if (eventNameFilter && eventNameValue !== 'all') {
			newParams.set(getParamName('eventName'), eventNameValue)
		} else {
			newParams.delete(getParamName('eventName'))
		}

		const currentSortBy = searchParams.get('sortBy')
		const currentSortDirection = searchParams.get('sortDirection')
		if (currentSortBy) newParams.set(getParamName('sortBy'), currentSortBy)
		if (currentSortDirection)
			newParams.set(getParamName('sortDirection'), currentSortDirection)

		setSearchParams(newParams)
	}

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			searchEvent()
		}
	}

	const handleSelectChange = () => {
		searchEvent()
	}

	const handleBrandChange = () => {
		if (qualificationRef.current) {
			qualificationRef.current.value = 'all'
		}
		// Use setTimeout to ensure the ref value is updated before calling searchEvent
		setTimeout(() => {
			searchEvent()
		}, 0)
	}

	const getSearchValue = () => {
		if (hasFiltersLocal && localSearchData?.search) {
			return localSearchData.search
		}
		return searchParams.get(getParamName('search')) || ''
	}

	const getStoreNameValue = () => {
		return searchParams.get(getParamName('storeName')) || 'all'
	}

	const getClassificationValue = () => {
		return searchParams.get(getParamName('classification')) || 'all'
	}

	const getQualificationValue = () => {
		if (hasFiltersLocal && localSearchData?.qualification) {
			return localSearchData.qualification
		}
		return searchParams.get(getParamName('qualification')) || 'all'
	}


	const getStatusValue = () => {
		if (hasFiltersLocal && localSearchData?.status) {
			return localSearchData.status
		}
		return searchParams.get(getParamName('status')) || 'all'
	}

	const getVoucherTypeValue = () => {
		// if (hasFiltersLocal && localSearchData?.voucherType) {
		//     return localSearchData.voucherType
		// }
		return searchParams.get(getParamName('voucherType')) || 'all'
	}

	const getEventIdxFilter = () => {
		return searchParams.get(getParamName('eventIdx')) || 'all'
	}

	const getIsUsedFilter = () => {
		return searchParams.get(getParamName('isUsed')) || 'all'
	}

	const getTicketTypeIdxFilter = () => {
		return searchParams.get(getParamName('ticketTypeIdx')) || 'all'
	}

	const getSeedTicketNameFilter = () => {
		return searchParams.get(getParamName('seedTicketName')) || 'all'
	}

	const getSeriesNameFilter = () => {
		return searchParams.get(getParamName('seriesName')) || 'all'
	}

	const getEventNameFilter = () => {
		return searchParams.get(getParamName('eventName')) || 'all'
	}

	const currentSearch = getSearchValue()
	const currentStoreName = getStoreNameValue()
	const currentClassification = getClassificationValue()
	const currentQualification = getQualificationValue()

	const currentStatus = getStatusValue()
	const currentVoucherType = getVoucherTypeValue()
	const currentEventIdx = getEventIdxFilter()
	const currentIsUsed = getIsUsedFilter()
	const currentTicketTypeIdx = getTicketTypeIdxFilter()
	const currentSeedTicketName = getSeedTicketNameFilter()
	const currentSeriesName = getSeriesNameFilter()
	const currentEventName = getEventNameFilter()

	const storeNameOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(storeNameList?.data?.items || []).map((storeName) => ({
			value: storeName.idx,
			label: storeName.storeName,
		})),
	]

	const classificationOptions = [
		{value: 'all', label: '== 모든 레벨 =='},
		...Object.entries(convertClassificationAsName).map(([value, label]) => ({
			value,
			label: `${label}`,
		})),
	]

	const statusOptions = [
		{value: 'all', label: '== 모든 상태 =='},
		{value: 'normal', label: '정상'},
		{value: 'banned', label: '차단됨'},
	]

	const voucherTypeOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(ticketTypesList?.data?.ticket_types || [])
			.sort((a, b) => a.idx - b.idx)
			.map((voucher) => ({
				value: voucher.idx,
				label: voucher.name,
			})),
	]

	const eventIdxOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(eventIdxList?.data || []).map((eventIdx) => ({
			value: eventIdx.idx,
			label: eventIdx.name,
		})),
	]

	const isUsedOptions = [
		{value: 'all', label: '== 모든 상태 =='},
		{value: 'true', label: '사용'},
		{value: 'false', label: '미사용'},
	]

	const ticketTypeIdxOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(ticketTypeIdxList?.data || []).map((ticketTypeIdx) => ({
			value: ticketTypeIdx.idx,
			label: ticketTypeIdx.name,
		})),
	]

	const seedTicketNameOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(seedTicketNameList?.data || []).map((seedTicketName) => ({
			value: seedTicketName.ticket_name,
			label: seedTicketName.ticket_name,
		})),
	]

	const seriesNameOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(seriesNameList?.data || []).map((seriesName) => ({
			value: seriesName.series_name,
			label: seriesName.series_name,
		})),
	]

	const eventNameOptions = [
		{value: 'all', label: '== 모든 유형 =='},
		...(eventNameList?.data || []).map((eventName) => ({
			value: eventName.event_name,
			label: eventName.event_name,
		})),
	]

	return (
		<Style.OutterWrapper>
			{!hideSearchInput && searchInputFullWidth && (
				<Style.ContainerSearch>
					<input
						placeholder={placeholder}
						defaultValue={currentSearch}
						ref={searchRef}
						onKeyDown={handleKeyDown}
					/>

					<DefaultButton small fitWidth expandHeight clickEvent={searchEvent}>
						검색
					</DefaultButton>
				</Style.ContainerSearch>
			)}
			<Style.Wrapper $noSearchInput={hideSearchInput}>
				{!hideSearchInput && !searchInputFullWidth && (
					<Style.ContainerSearch>
						<input
							placeholder={placeholder}
							defaultValue={currentSearch}
							ref={searchRef}
							onKeyDown={handleKeyDown}
						/>

						<DefaultButton small fitWidth expandHeight clickEvent={searchEvent}>
							검색
						</DefaultButton>
					</Style.ContainerSearch>
				)}

				{hasFilters && (
					<Style.ContainerSelect>
						{storeNameFilter && (
							<div>
								<h2>매장명</h2>
								<select
									ref={storeNameRef}
									defaultValue={currentStoreName}
									onChange={handleSelectChange}
								>
									{storeNameOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{classificationFilter && (
							<div>
								<h2>분류</h2>
								<select
									ref={classificationRef}
									defaultValue={currentClassification}
									onChange={handleSelectChange}
								>
									{classificationOptions.map((option, index) => (
										<option key={index} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{qualificationFilter && (
							<>
								<div>
									<h2>브랜드</h2>
									<InfiniteSelect
										ref={franchiseRef}
										defaultValue={currentFranchise}
										options={examList || []}
										onChange={handleBrandChange}
										isLoading={examLoading}
										loadMore={examLoadMore}
										hasMore={examHasMore}
										width='200px'
									/>
								</div>
								<div>
									<h2>자격</h2>

									<select
										ref={qualificationRef}
										defaultValue={currentQualification}
										onChange={handleSelectChange}
									>
										{levelData.map((level, index) => (
											<option key={index} value={level.value}>
												{level.label}
											</option>
										))}
									</select>
								</div>
							</>
						)}

						{statusFilter && (
							<div>
								<h2>상태</h2>
								<select
									ref={statusRef}
									defaultValue={currentStatus}
									onChange={handleSelectChange}
								>
									{statusOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{voucherTypeFilter && (
							<div>
								<h2>매장 이용권 유형</h2>
								<select
									ref={voucherTypeRef}
									defaultValue={currentVoucherType}
									onChange={handleSelectChange}
								>
									{voucherTypeOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{eventIdxFilter && (
							<div>
								<h2>대회명</h2>
								<select
									ref={eventIdxRef}
									defaultValue={currentEventIdx}
									onChange={handleSelectChange}
								>
									{eventIdxOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{isUsedFilter && (
							<div>
								<h2>사용 여부</h2>
								<select
									ref={isUsedRef}
									defaultValue={currentIsUsed}
									onChange={handleSelectChange}
								>
									{isUsedOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{ticketTypeIdxFilter && (
							<div>
								<h2>시드권 타입</h2>
								<select
									ref={ticketTypeIdxRef}
									defaultValue={currentTicketTypeIdx}
									onChange={handleSelectChange}
								>
									{ticketTypeIdxOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{seedTicketNameFilter && (
							<div>
								<h2>시드권 이름</h2>
								<select
									ref={seedTicketNameRef}
									defaultValue={currentSeedTicketName}
									onChange={handleSelectChange}
								>
									{seedTicketNameOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{seriesNameFilter && (
							<div>
								<h2>시리즈 이름</h2>
								<select
									ref={seriesNameRef}
									defaultValue={currentSeriesName}
									onChange={handleSelectChange}
								>
									{seriesNameOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}

						{eventNameFilter && (
							<div>
								<h2>대회 이름</h2>
								<select
									ref={eventNameRef}
									defaultValue={currentEventName}
									onChange={handleSelectChange}
								>
									{eventNameOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
						)}
					</Style.ContainerSelect>
				)}
			</Style.Wrapper>
		</Style.OutterWrapper>
	)
}

export default Search
